import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import { ProjectHours } from 'api/project';
import { GlassCrack16 } from 'components/atoms/icons/glass-crack';
import { ClassNameOnlyProps } from 'components/ozone-component';
import { Caption01 } from 'components/styles';

const Wrapper = styled.span<{ hasMarker: boolean }>`
  ${ifProp(
    'hasMarker',
    css`
      padding-bottom: 16px;
    `,
  )};
`;

const Bar = styled.div<{ height: number }>`
  display: inline-block;
  position: relative;
  height: ${({ height }) => `${height}px`};
`;

const Fill = styled.div`
  display: inline-block;
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
`;

const NoBudget = styled(GlassCrack16)<{ size: number }>`
  color: white;
  position: absolute;
  transform: translateX(-50%);
  left: 50%;
  top: ${({ size }) => `calc(-1px * ${size} / 8)`};
  width: ${({ size }) => `calc(5px / 4 * ${size})`};
  height: ${({ size }) => `calc(5px / 4 * ${size})`};
`;

const OverBudget = styled(GlassCrack16)<{ size: number }>`
  color: white;
  position: absolute;
  right: 10%;
  top: ${({ size }) => `calc(-1px * ${size} / 8)`};
  width: ${({ size }) => `calc(5px / 4 * ${size})`};
  height: ${({ size }) => `calc(5px / 4 * ${size})`};
`;

const Mark = styled.div<{ at: number }>`
  position: absolute;
  background: var(--coolest-gray-40);
  width: 2px;
  height: 100%;
  left: ${({ at }) => `calc(${at}% - 1px)`};
  &:after {
    ${Caption01};
    color: #000;
    opacity: 0.5;
    content: ${({ at }) => `'${at}%'`};
    position: absolute;
    bottom: -20px;
    transform: translate(-50%);
  }
`;

export const Progress: FC<
  Omit<ProjectHours, 'project_name'> & {
    width: string | number;
    height: number;
    hasMarker?: boolean;
  } & ClassNameOnlyProps
> = ({
  className,
  total_minutes,
  budgetMinutes,
  width,
  height,
  hasMarker = false,
}) => {
  const percentage = budgetMinutes ? (total_minutes * 100) / budgetMinutes : 0;
  const over_budget = budgetMinutes != null && percentage > 100;

  const background =
    budgetMinutes == null
      ? 'var(--coolest-gray-20)'
      : percentage <= 80
      ? 'var(--blue-40)'
      : percentage <= 100
      ? 'var(--yellow-40)'
      : 'var(--red-50)';
  const fill = `${Math.min(Math.max(0, percentage), 100)}%`;

  return (
    <Wrapper hasMarker={hasMarker} style={{ width }}>
      <Bar className={className} style={{ width }} height={height}>
        <Fill style={{ background: 'var(--coolest-gray-20)', width }} />
        <Fill style={{ background, width: fill }} />
        {budgetMinutes == null ? <NoBudget size={height} /> : null}
        {over_budget ? <OverBudget size={height} /> : null}
        {hasMarker ? (
          <>
            <Mark at={50} />
            <Mark at={80} />
          </>
        ) : null}
      </Bar>
    </Wrapper>
  );
};
